/* eslint-disable react/jsx-no-target-blank */
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { analytics } from '@src/utility/analytics';
import {
  Bold,
  BoldValues,
  findClosestBoldValueFont,
  getBoldValuesFromCustomFonts,
  getCustomFontFromPostScriptName,
} from '@src/utility/fonts';
import lodash from 'lodash';
import { observer } from 'mobx-react-lite';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { KARAOKE_TRACK_NUMBER } from '../../stores/VideoCreatorStore';
import { numericalInputStyles } from '../../styles/mainStyle';
import UpDownIcon from '../../svgs/UpDownIcon';
import { FONT_SIZE_VALUES } from '../../utility/general';
import { getMinMaxYPosition } from '../../videoTranscriptionProcessor/utils';
import Checkbox from '../common/Checkbox';
import ColorDropdownSelect from '../common/ColorDropdownSelect';
import FontDropdownSelect from '../common/FontDropdownSelect';
import { TextIcon } from '../common/icons';
import ItemDropdownSelect from '../common/ItemDropdownSelect';
import KaraokeBackground from '../common/KaraokeBackground';
import KaraokeShadow from '../common/KaraokeShadow';
import SaveOrCancelTemplate from '../common/SaveOrCancelTemplate';
import TextBackgroundRadius from '../common/TextBackgroundRadius';
import TextBoldAlignFormatting from '../common/TextBoldAlignFormatting';
import TextBrandingTemplates from '../common/TextBrandingTemplates';
import SpinningLoading from '../SpinningLoading';
import PrimaryActionButton from './PrimaryActionButton';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';
import SliderSelect from './SliderSelect';
import { TextAnimationSettings } from './TextAnimationSettings';
import { TextInput } from './TextInput';

import type { ElementState } from '../../renderer/ElementState';
import type { KaraokeConfig } from '../../videoTranscriptionProcessor/types/karaokeTypes';
import type { LowerThirdTemplate } from '../textProcessor/TextBrandingService';

interface TextSettingsProps {
  activeElement: ElementState | null | undefined;
}

enum Action {
  bold = 'Bold',
  italic = 'Italic',
  capitalize = 'Capitalize',
  align = 'Align',
  // bgColor = 'Background Color',
}

enum Align {
  leftAlign = 'Left Align',
  midAlign = 'Middle Align',
  rightAlign = 'Right Align',
}

export const AlignValues = {
  [Align.leftAlign]: '0%',
  [Align.midAlign]: '50%',
  [Align.rightAlign]: '100%',
};

enum Colors {
  black = 'black',
  white = 'white',
  red = 'red',
  green = 'green',
  orange = 'orange',
  blue = 'blue',
  yellow = 'yellow',
}

const formatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  maximumFractionDigits: 3,
  minimumFractionDigits: 0,
});

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;

  position: relative;
`;

const Position = styled.div`
  flex: 1;
`;
const FontSize = styled.div`
  flex: 1;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

const BackgroundShadowGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const SliderGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const FontColorGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 8px;
`;

const TextTemplateGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.div``;

const PositionContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
`;

const AddTextButton = styled(PrimaryActionButton)`
  height: 40px;
  font-size: 14px;
  display: flex;
  cursor: pointer;
  svg {
    margin-right: 8px;
  }
`;

const TextWrapper = styled.div`
  z-index: 2;
  cursor: pointer;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 12px;
`;

const HorizontalLine = styled.div`
  background-color: #333;
  width: 100%;
  height: 1px;
`;

const FieldName = styled.span`
  color: #848484;
  font-family: Inter, san-serif;
  font-size: 10px;
  font-weight: 500;
`;

export const TextSettings = observer(
  forwardRef<any, TextSettingsProps>((props, ref) => {
    const videoCreator = useVideoCreatorStore();
    const { editorStore } = useStore();
    const { source } = props.activeElement || {};
    const config = videoCreator.textProducer.getTextConfig();
    const template = videoCreator.textBrandingService
      .selectedBrandingTemplate as LowerThirdTemplate;
    const textWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (source && source.track !== KARAOKE_TRACK_NUMBER) {
        videoCreator.textProducer.setTextConfig(source);
      }
    }, [source, videoCreator.currentVideo?.aspectRatio]);

    useEffect(() => {
      if (!source) {
        videoCreator.textProducer.setTextConfig({
          ...config,
          time: videoCreator.time,
        });
      }
    }, [videoCreator.time, template]);

    useEffect(() => {
      if (videoCreator.textBrandingService.isLoading) {
        textWrapperRef.current?.scrollIntoView();
      }
    }, [videoCreator.textBrandingService.isLoading]);

    useEffect(
      () => () => {
        if (videoCreator.textBrandingService.isTemplateModified) {
          videoCreator.textBrandingService.isTemplateModified = false;
        }
      },
      [],
    );

    const [addNewTemplate, toggleAddNewTemplate] = useState<boolean>(false);
    const [templateName, setTemplateName] = useState<string>();
    const [templateNameError, setTemplateNameError] = useState<boolean>(false);
    const [allowCustomTemplateSave, setAllowCustomTemplateSave] =
      useState<boolean>(false);
    const [shouldUseTemplatePosition, setShouldUseTemplatePosition] =
      useState<boolean>(true);

    const hasStickyBottom =
      addNewTemplate ||
      allowCustomTemplateSave ||
      videoCreator.textBrandingService.isTemplateModified;

    const xAlignment = config?.x_alignment || '0%';
    const selectedFontFamily = config?.font_family;

    const getDefaultXAlign = () => {
      switch (xAlignment) {
        case '0%':
          return Align.leftAlign;
        case '50%':
          return Align.midAlign;
        case '100%':
          return Align.rightAlign;
        default:
          return Align.leftAlign;
      }
    };

    const brandCustomFonts =
      videoCreator.organization?.branding?.customFonts || [];
    const getSelectedBold = () => {
      const weight = config?.font_weight;
      const customFont = getCustomFontFromPostScriptName(
        selectedFontFamily,
        brandCustomFonts,
      );
      if (customFont) return customFont.fontSubfamily;
      if (!weight) return Bold.normal;
      for (const key in BoldValues) {
        if (BoldValues[key as Bold] === Number(weight)) return key as Bold;
      }
      return Bold.normal;
    };

    const alignType = getDefaultXAlign();
    const selectedBold = getSelectedBold();
    const selectFillColor = config?.fill_color;
    const selectedBackgroundColor = config?.background_color;
    const selectedBackgroundAlignThreshold = config?.background_align_threshold;
    const selectedBackgroundBorderRadius = config?.background_border_radius;
    const selectedBackgroundOpacity = parseFloat(
      !selectedBackgroundColor || selectedBackgroundColor === 'transparent'
        ? '0'
        : selectedBackgroundColor.split(',')[3]?.split(')')[0]?.trim() ?? '1',
    );
    const selectedShadow = useMemo(
      () =>
        config.shadow_color && config.shadow_color !== 'false'
          ? {
              color: config.shadow_color as string,
              blur: config.shadow_blur as number,
              x: config.shadow_x as number,
              y: config.shadow_y as number,
            }
          : undefined,
      [config],
    );

    const getDefaultActions = () => {
      let actions: Action[] = [];
      if (config?.font_style === 'italic') {
        actions.push(Action.italic);
      } else {
        actions = actions.filter(action => action !== Action.italic);
      }

      if (config?.text_transform === 'uppercase') {
        actions.push(Action.capitalize);
      } else {
        actions = actions.filter(action => action !== Action.capitalize);
      }

      if (config?.font_weight && config?.font_weight !== '400') {
        actions.push(Action.bold);
      } else {
        actions = actions.filter(action => action !== Action.bold);
      }
      actions.push(Action.align);

      return actions;
    };

    const [selectedActions, setSelectedActions] =
      useState<Action[]>(getDefaultActions());

    useEffect(() => {
      setSelectedActions(getDefaultActions());
    }, [config]);

    const posY = config?.y || '50%';

    const { font_size: fontSize } = config;

    const minMaxYPosition = getMinMaxYPosition(
      videoCreator.rootStore.videoSourceStore.source?.height,
      fontSize.toString(),
    );

    const expandPropertyMap = (
      propertyMap: Record<string, any>,
    ): Record<string, any> => {
      if ('font_size' in propertyMap) {
        // eslint-disable-next-line no-param-reassign
        propertyMap.font_size = videoCreator.textProducer.convertToVh(
          propertyMap.font_size,
        );
      }
      return propertyMap;
    };

    // Helper function to modify text element properties using headless editor or fallback to legacy
    const modifyProperty = (property: Record<string, any>) => {
      if (
        editorStore?.headlessEnabled &&
        editorStore?.editor &&
        props.activeElement?.source?.id
      ) {
        // Use headless editor UpdateElementCommand
        const success = editorStore.editor.commandUpdateElement(
          props.activeElement.source.id,
          expandPropertyMap(property),
        );
        videoCreator.textProducer.modifyProperty(property, false, null); // null to prevent source update
        if (!success) {
          console.warn(`Failed to update text element properties:`, property);
        }
      } else {
        // Fallback to legacy method
        videoCreator.textProducer.modifyProperty(property);
      }
    };

    const modifyPropertyDebounced = lodash.debounce(modifyProperty, 250);
    const isKaraokeText = props.activeElement?.track === KARAOKE_TRACK_NUMBER;

    // Manage template position checkbox state
    useEffect(() => {
      if (!source || isKaraokeText) {
        // For new elements or karaoke, default to checked
        setShouldUseTemplatePosition(true);
        return;
      }

      if (
        !template ||
        !videoCreator.textBrandingService.hasTemplatePositioning(template)
      ) {
        // No template or no positioning data
        setShouldUseTemplatePosition(false);
        return;
      }

      // Check if current element position matches template position
      const positionMatches =
        videoCreator.textBrandingService.doesElementMatchTemplatePosition(
          source,
          template,
        );
      setShouldUseTemplatePosition(positionMatches);
    }, [source, template, isKaraokeText, config?.x, config?.y]);

    // Handle checkbox toggle to apply/remove template positioning
    const handlePositionCheckboxToggle = async () => {
      const newValue = !shouldUseTemplatePosition;
      setShouldUseTemplatePosition(newValue);
      if (!template || !source) return;
      if (newValue) {
        // Apply template positioning if not yet applied
        const currentlyMatches =
          videoCreator.textBrandingService.doesElementMatchTemplatePosition(
            source,
            template,
          );
        if (!currentlyMatches) {
          const positionConfig: { x?: string; y?: string } = {};
          if (template.xPos !== undefined && template.xPos !== null) {
            positionConfig.x =
              videoCreator.textBrandingService.formatPositionValue(
                template.xPos,
              );
          }
          if (template.yPos !== undefined && template.yPos !== null) {
            positionConfig.y =
              videoCreator.textBrandingService.formatPositionValue(
                template.yPos,
              );
          }
          if (Object.keys(positionConfig).length > 0) {
            videoCreator.textBrandingService.isApplyingTemplatePosition = true;
            modifyProperty(positionConfig);
            setTimeout(() => {
              videoCreator.textBrandingService.isApplyingTemplatePosition =
                false;
            }, 200);
          }
        }
      }
    };

    const handleToggleAction = (
      action: Action,
      propertyName: keyof KaraokeConfig,
      normalState: string,
      requiredState: string,
    ) => {
      const isSelected = selectedActions.includes(action);

      const state = isSelected ? normalState : requiredState;
      modifyProperty({ [propertyName]: state });
    };

    const handleSetBold = (
      bold: string,
      fontWeight: number,
      fontScriptName?: string,
    ) => {
      const state = selectedBold === bold ? null : bold;
      const value = state ? fontWeight : BoldValues[Bold.normal];
      modifyProperty({
        font_weight: value.toString(),
        ...(fontScriptName && { font_family: fontScriptName }),
      });
    };

    const handleSetFont = (fontScriptNameInput?: string) => {
      let fontWeight = config.font_weight;
      let fontScriptName = fontScriptNameInput;
      if (fontScriptName) {
        const isCustomFont = !!getCustomFontFromPostScriptName(
          fontScriptName,
          brandCustomFonts,
        );
        if (isCustomFont) {
          const { fontsByWeight } = getBoldValuesFromCustomFonts(
            fontScriptName,
            brandCustomFonts,
          );
          const closestBold = findClosestBoldValueFont(fontsByWeight);
          if (closestBold) {
            fontWeight = closestBold.weight.toString();
            fontScriptName = closestBold.font.postScriptName;
          }
        } else {
          fontWeight = BoldValues[Bold.normal].toString();
        }
      }
      modifyProperty({
        font_family: fontScriptName,
        font_weight: fontWeight,
      });
    };

    const renderFullTextSettings = () => (
      <>
        <HorizontalLine />
        {!isKaraokeText && (
          <Item style={{ display: 'flex', flexDirection: 'column' }}>
            <FieldName>Text</FieldName>
            <TextInput
              text={config?.text || ''}
              modifyElement={text => {
                if (text !== config?.text || '') {
                  modifyProperty({ text });
                }
              }}
            />
          </Item>
        )}
        <TextTemplateGroup>
          <TextBrandingTemplates
            ref={ref}
            hasStickyBottom={hasStickyBottom}
            addNewTemplate={addNewTemplate}
            toggleAddNewTemplate={toggleAddNewTemplate}
            setTemplateNameError={setTemplateNameError}
            templateNameError={templateNameError}
            setTemplateName={setTemplateName}
            hasActiveElement={
              !!source && !videoCreator.textProducer.creatingNewText
            }
            type={isKaraokeText ? 'karaoke' : 'basic'}
            shouldUseTemplatePosition={shouldUseTemplatePosition}
          />
          {!isKaraokeText &&
            template &&
            videoCreator.textBrandingService.hasTemplatePositioning(
              template,
            ) && (
              <Checkbox
                label="Apply to default position"
                color={shouldUseTemplatePosition ? '#4AD067' : 'transparent'}
                borderColor={shouldUseTemplatePosition ? '#4AD067' : '#9B9B9B'}
                labelColor="#F3E9D7"
                checked={shouldUseTemplatePosition}
                onClick={handlePositionCheckboxToggle}
              />
            )}
        </TextTemplateGroup>
        <FontColorGroup>
          <FontDropdownSelect
            ref={ref}
            hasStickyBottom={hasStickyBottom}
            action={(_, value) => handleSetFont(value || undefined)}
            propertyName="font_family"
            defaultValue={selectedFontFamily || 'Inter'}
            fontWeight={config?.font_weight}
            type="basic"
          />

          <ColorDropdownSelect
            action={(propertyName, value) =>
              modifyProperty({
                [propertyName]: value,
              })
            }
            propertyName="fill_color"
            defaultValue={selectFillColor || Colors.black}
          />
        </FontColorGroup>

        <SliderGroup>
          <Position>
            <SliderSelect
              title="Position"
              Icon={<UpDownIcon />}
              action={modifyPropertyDebounced}
              defaultValue={posY}
              unit="%"
              propertyName="y"
              reverse
              min={100 - minMaxYPosition[1]}
              max={100 - minMaxYPosition[0]}
              snapPoints={[
                {
                  value:
                    (100 - minMaxYPosition[1] + 100 - minMaxYPosition[0]) / 2,
                  tooltip: 'Center Point',
                  reverse: true,
                },
              ]}
            />
          </Position>

          <FontSize>
            <ItemDropdownSelect
              ref={ref}
              hasStickyBottom={hasStickyBottom}
              action={(propertyName, value) => {
                modifyProperty({
                  [propertyName]: value,
                });
              }}
              propertyName="font_size"
              defaultValue={Math.round(fontSize || 32).toString()}
              values={FONT_SIZE_VALUES.map(value => ({
                caption: `${value}`,
                value: `${value}`,
              }))}
            />
          </FontSize>
        </SliderGroup>
        <ActionGroup>
          <TextBoldAlignFormatting
            ref={ref}
            alignType={alignType}
            handleToggleAction={handleToggleAction}
            handleSetBold={handleSetBold}
            hasStickyBottom={hasStickyBottom}
            selectedFontFamily={selectedFontFamily}
            selectedBold={selectedBold}
            selectedActions={selectedActions}
            toggleAlign={align => {
              modifyProperty({
                x_alignment: AlignValues[align],
              });
            }}
          />
        </ActionGroup>
        <BackgroundShadowGroup>
          <KaraokeBackground
            action={property => {
              modifyProperty({ ...property });
            }}
            defaultValue={selectedBackgroundColor}
            opacity={selectedBackgroundOpacity}
            backgroundAlignThreshold={selectedBackgroundAlignThreshold}
          />
          <TextBackgroundRadius
            value={selectedBackgroundBorderRadius}
            action={property => {
              modifyProperty({ ...property });
            }}
          />
          <KaraokeShadow
            type="basic"
            action={property => {
              modifyProperty({ ...property });
            }}
            value={selectedShadow || undefined}
          />
        </BackgroundShadowGroup>
        <TextAnimationSettings activeElement={props.activeElement} />

        <PositionContent>
          <Item>
            <PropertyCaption>Start</PropertyCaption>
            <PropertyNumericalInput
              propertyName="time"
              defaultValue={config.time || 0}
              unit="s"
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                modifyProperty({
                  [property]: value,
                });
              }}
            />
          </Item>
          <Item>
            <PropertyCaption>End</PropertyCaption>
            <PropertyNumericalInput
              propertyName="time"
              defaultValue={formatter.format(
                (parseFloat(config.time) || 0) +
                  (parseFloat(config.duration) || 4),
              )}
              unit="s"
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                const duration = formatter.format(
                  parseFloat(value) - parseFloat(config.time) || 0,
                );
                modifyProperty({ duration });
              }}
            />
          </Item>
          <Item>
            <PropertyCaption>Length</PropertyCaption>
            <PropertyNumericalInput
              propertyName="duration"
              defaultValue={config.duration || 4}
              unit="s"
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                modifyProperty({
                  [property]: value,
                });
              }}
            />
          </Item>
        </PositionContent>
        <SaveOrCancelTemplate
          addNewTemplate={addNewTemplate}
          setTemplateName={setTemplateName}
          setTemplateNameError={setTemplateNameError}
          templateName={templateName}
          templateNameError={templateNameError}
          toggleAddNewTemplate={toggleAddNewTemplate}
          allowCustomTemplateSave={allowCustomTemplateSave}
          setAllowCustomTemplateSave={setAllowCustomTemplateSave}
          type={isKaraokeText ? 'karaoke' : 'basic'}
          setting="text-settings"
        />
      </>
    );

    // Helper function to create text element using headless editor or fallback to legacy
    const createTextElement = async () => {
      if (!editorStore?.headlessEnabled) {
        // Fallback to legacy method
        await videoCreator.textProducer.produceText();
        return;
      }

      // Track analytics like the original method
      analytics.track('create_text_element', {
        storyId: videoCreator.story?.id,
        storyTitle: videoCreator.story?.title,
        videoId: videoCreator.currentVideo?.id,
        videoTitle: videoCreator.currentVideo?.title,
      });

      // Set creatingNewText flag to prevent template switching during creation
      videoCreator.textProducer.creatingNewText = true;

      // Get text producer config for smart positioning and aspect ratio defaults
      const textConfig = videoCreator.textProducer.initializeConfig();
      const currentConfig = videoCreator.textProducer.getTextConfig();

      // Smart positioning logic (from original produceText)
      let targetY = textConfig.y;
      const activeElement = videoCreator.getActiveElement();
      const activeSource = activeElement?.source;

      if (
        activeSource?.type === 'text' &&
        activeSource.track !== KARAOKE_TRACK_NUMBER
      ) {
        const height = Math.ceil(parseFloat(activeSource.font_size) * 1.7);
        targetY = `${parseFloat(activeSource.y || '50') + height}%`;
        const minMaxYPos = getMinMaxYPosition(
          videoCreator.rootStore.videoSourceStore.source?.height,
          videoCreator.textProducer
            .getFontSize(currentConfig.font_size)
            .toString(),
        );
        const max = 100 - minMaxYPos[0];
        if (parseFloat(targetY) > max) {
          targetY = textConfig.y;
        }
      }

      // Apply template positioning if available, otherwise use default positioning
      let finalX = '50%';
      let finalY = targetY;

      if (
        !isKaraokeText &&
        template &&
        videoCreator.textBrandingService.hasTemplatePositioning(template)
      ) {
        if (template.xPos !== undefined && template.xPos !== null) {
          finalX = videoCreator.textBrandingService.formatPositionValue(
            template.xPos,
          );
        }
        if (template.yPos !== undefined && template.yPos !== null) {
          finalY = videoCreator.textBrandingService.formatPositionValue(
            template.yPos,
          );
        }
      }

      // Prepare properties for the text element
      const {
        /* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
        id: _id,
        track: _track,
        time: _time,
        /* eslint-enable @typescript-eslint/no-unused-vars, no-unused-vars */
        transcript_effect,
        transcript_split,
        transcript_placement,
        transcript_maximum_length,
        transcript_color,
        transcript_source,
        ...config
      } = currentConfig;
      const textProps = {
        ...textConfig,
        ...config,
        text: textConfig.text || '',
        x: finalX,
        y: finalY,
        font_size: videoCreator.textProducer.convertToVh(
          currentConfig.font_size || '32',
        ),
        time: videoCreator.time,
      };

      if (
        transcript_effect ||
        transcript_split ||
        transcript_placement ||
        transcript_maximum_length ||
        transcript_color ||
        transcript_source
      ) {
        console.warn('Karaoke-specific fields found at createTextElement', {
          ...config,
        });
      }

      // Create element using headless editor command
      const element = editorStore.editor.commandCreateTextElement(textProps);

      if (element) {
        // Handle template association like original method
        const templateId = videoCreator.textBrandingService.selectedTemplateId;

        if (templateId) {
          const existingData =
            videoCreator.currentVideo?.extraElementData || {};
          videoCreator.currentVideo!.extraElementData = {
            ...existingData,
            lowerThirdTemplates: {
              ...(existingData?.lowerThirdTemplates || {}),
              [element.id]: templateId,
            },
          };
        }

        // Auto-select element after delay
        setTimeout(async () => {
          console.log(
            `[TextSettings] Auto-selecting newly created text element: ${element.id}`,
          );
          await videoCreator.setActiveElements(element.id);
          videoCreator.textProducer.creatingNewText = false;
        }, 300);
      }
    };

    return (
      <Main>
        {videoCreator.textBrandingService.isLoading && (
          <SpinningLoading
            positionTop="20px"
            text={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>Saving template</span>
              </div>
            }
          />
        )}
        {videoCreator.textBrandingService.isPropsChanged && (
          <SpinningLoading
            positionTop="20px"
            text={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>Creating Custom Template</span>
              </div>
            }
          />
        )}
        <TextWrapper ref={textWrapperRef}>
          <AddTextButton type="button" onClick={createTextElement}>
            <TextIcon height="20" width="20" />
            Add New Text Element
          </AddTextButton>
        </TextWrapper>

        {props.activeElement && renderFullTextSettings()}
      </Main>
    );
  }),
);
